import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { Store, Star, ShieldCheck, Filter, ShoppingCart, Users, Upload } from 'lucide-react';
import Leaderboard from '@/components/Leaderboard';

const SORT_KEYS = [
  { id: 'popular', key: 'market_sort_popular' },
  { id: 'recent', key: 'market_sort_recent' },
  { id: 'price_asc', key: 'market_sort_price_asc' },
  { id: 'price_desc', key: 'market_sort_price_desc' },
];

const CATS = ['all', 'man', 'zone', 'ato', 'blob', 'slob', 'endgame'];

const CAT_KEY = {
  all: 'market_cat_all',
  man: 'cat_man',
  zone: 'cat_zone',
  ato: 'cat_ato',
  blob: 'cat_blob',
  slob: 'cat_slob',
  endgame: 'cat_endgame',
};

export default function Marketplace() {
  const { t } = useLang();
  const [tab, setTab] = useState('listings');
  const [coaches, setCoaches] = useState([]);
  const [listings, setListings] = useState([]);
  const [sort, setSort] = useState('popular');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/marketplace/coaches')
      .then(r => setCoaches(r.data.coaches || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { sort, category };
    api.get('/marketplace/listings', { params })
      .then(r => setListings(r.data.listings || []))
      .finally(() => setLoading(false));
  }, [sort, category]);

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_marketplace')}</div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
            <Store className="w-9 h-9 text-gold" /> {t('market_h1')}
          </h1>
          <p className="text-gray-400 mt-2">{t('market_subtitle')}</p>
        </div>
        <Link
          to="/profile"
          data-testid="market-sell-cta"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light"
          title={t('market_publish_my_play_title')}
        >
          <Upload className="w-4 h-4" /> {t('market_publish_my_play')}
        </Link>
      </div>

      <div className="mb-6 bg-gradient-to-r from-gold/10 to-transparent border border-gold/30 rounded-lg p-4 flex items-start gap-3" data-testid="market-sell-banner">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center">
          <Upload className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-h uppercase text-sm text-gold tracking-wider">{t('market_banner_title')}</div>
          <p className="text-xs text-gray-300 mt-1 leading-relaxed">
            {t('market_banner_body')}
          </p>
        </div>
        <Link to="/profile" className="self-center text-[11px] text-gold hover:underline font-h uppercase tracking-wider">
          {t('market_banner_start')}
        </Link>
      </div>

      <div className="flex gap-2 mb-6 border-b border-gold/20">
        {[
          { id: 'listings', key: 'market_tab_listings', icon: ShoppingCart },
          { id: 'coaches', key: 'market_tab_coaches', icon: ShieldCheck },
          { id: 'leaderboard', key: 'market_tab_leaderboard', icon: Star },
        ].map(tt => (
          <button key={tt.id} onClick={() => setTab(tt.id)} data-testid={`market-tab-${tt.id}`}
            className={`px-4 py-2 font-h uppercase tracking-wider text-sm transition border-b-2 inline-flex items-center gap-2 ${tab === tt.id ? 'border-gold text-gold' : 'border-transparent text-gray-400 hover:text-gold'}`}>
            <tt.icon className="w-4 h-4" /> {t(tt.key)}
          </button>
        ))}
      </div>

      {tab === 'leaderboard' && <Leaderboard />}

      {tab === 'listings' && (
        <>
          <div className="mb-5 bg-card-steel border border-gold/20 rounded-lg p-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 flex-wrap">
              <Filter className="w-4 h-4 text-gold me-2" />
              {CATS.map(c => (
                <button key={c} onClick={() => setCategory(c)} data-testid={`market-cat-${c}`}
                  className={`px-3 py-1.5 rounded text-xs font-h uppercase tracking-wider border ${category === c ? 'bg-gold text-navy border-gold' : 'border-steel text-gray-300 hover:border-gold/60'}`}>
                  {t(CAT_KEY[c])}
                </button>
              ))}
            </div>
            <div className="ms-auto flex gap-1.5">
              {SORT_KEYS.map(s => (
                <button key={s.id} onClick={() => setSort(s.id)} data-testid={`market-sort-${s.id}`}
                  className={`px-3 py-1.5 rounded text-xs font-h uppercase tracking-wider border ${sort === s.id ? 'bg-gold text-navy border-gold' : 'border-steel text-gray-300 hover:border-gold/60'}`}>
                  {t(s.key)}
                </button>
              ))}
            </div>
          </div>

          {loading && <div className="text-gray-400">{t('loading')}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map(p => (
              <Link key={p.id} to={`/marketplace/play/${p.id}`} data-testid={`listing-${p.id}`}
                className="bg-card-steel border-2 border-gold/40 rounded-lg overflow-hidden hover-lift block">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      {p.coach_photo ? (
                        <img src={p.coach_photo} className="w-10 h-10 rounded-full border-2 border-gold object-cover" alt="" />
                      ) : (
                        <div className="w-10 h-10 rounded-full border-2 border-gold bg-gold/10" />
                      )}
                      <div>
                        <div className="text-sm text-white font-semibold flex items-center gap-1">
                          {p.coach_name}
                          {p.coach_elite && <ShieldCheck className="w-3.5 h-3.5 text-gold" />}
                        </div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-widest">{p.category} · {p.subcategory}</div>
                      </div>
                    </div>
                    <div className="font-h text-2xl text-gold">${p.price?.toFixed(2)}</div>
                  </div>
                  <div className="font-h uppercase text-xl text-white mb-2">{p.name}</div>
                  <p className="text-sm text-gray-300 line-clamp-2 mb-3">{p.listing_blurb || p.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gold font-mono border border-gold/30 rounded px-2 py-0.5">{Math.round((p.success_rate || 0.6) * 100)}% {t('market_success')}</span>
                    <span className="text-gray-400">{p.purchases_count || 0} {t('market_sales')}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {!loading && !listings.length && (
            <div className="text-center text-gray-400 py-12 border-2 border-dashed border-gold/30 rounded-lg">
              {t('market_empty')}
            </div>
          )}
        </>
      )}

      {tab === 'coaches' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {coaches.map(c => (
            <Link key={c.id} to={`/marketplace/coach/${c.coach_id}`} data-testid={`coach-${c.coach_id}`}
              className="bg-card-steel border-2 border-gold/40 rounded-lg p-5 hover-lift block">
              <div className="flex items-center gap-3 mb-3">
                {c.photo_url ? (
                  <img src={c.photo_url} className="w-16 h-16 rounded-full border-2 border-gold object-cover" alt="" />
                ) : (
                  <div className="w-16 h-16 rounded-full border-2 border-gold bg-gold/10 flex items-center justify-center text-gold font-h text-2xl">
                    {c.display_name?.[0]}
                  </div>
                )}
                <div>
                  <div className="font-h uppercase text-xl text-white flex items-center gap-1">
                    {c.display_name}
                    <ShieldCheck className="w-4 h-4 text-gold" title={t('market_verified_elite')} />
                  </div>
                  <div className="text-xs text-gold uppercase tracking-widest">{c.credentials}</div>
                </div>
              </div>
              <p className="text-sm text-gray-300 line-clamp-2 mb-3">{c.bio}</p>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1 text-gold">
                  <Star className="w-3 h-3 fill-gold" /> {c.rating?.toFixed(1)}
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Users className="w-3 h-3" /> {c.students} {t('market_students')}
                </div>
                <div className="text-gold">{c.listing_count} {t('market_plays_count')}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}
